import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import api from '../../../services/api';
import { COMPONENT_QUERY } from '../../../constants/api';
import {
  transformHeaders,
  transformData,
  transformMonthlyData,
  channelAndAudienceHeaders,
  verifyRowData,
  formateGrandTotalColumnsData,
  getPayload,
  setErrorsFunction,
  appendChannelFilter,
  formatStoreOptions,
  stableObject,
} from '../../../constants/utils';

const useCurrentPlanSpendData = ({
  WEEKLY_SPEND_COMPONENT,
  MONTHLY_SPEND_COMPONENT,
  WEEKLY_HEADERS_COMPONENT,
  DOWNLOAD_FILE_NAME_WEEKLY,
  DOWNLOAD_FILE_NAME_MONTHLY,
  DATA_KEY,
  isOppsChannel = false,
}) => {
  // Track initial mount and data fetching status
  const currentPlanIsFirstMount = useRef(true);
  const currentPlanDataFetchedRef = useRef({
    weekly: false,
    headers: false,
    monthly: false,
  });

  // Use ref to track if we've already fetched data for each view
  const currentPlanViewSwitchTracker = useRef({
    weekly: { fetched: false },
    monthly: { fetched: false },
  });

  // Initialize with true to show loading state immediately
  const [currentPlanInitialLoading, setCurrentPlanInitialLoading] = useState(true);
  const currentPlanDataFetchInitiatedRef = useRef(false);

  // Data state
  const [currentPlanWeeklyTableHeaders, setCurrentPlanWeeklyTableHeaders] = useState([]);
  const [currentPlanWeeklyRowData, setCurrentPlanWeeklyRowData] = useState([]);
  const [currentPlanMonthlyTableHeaders, setCurrentPlanMonthlyTableHeaders] = useState([]);
  const [currentPlanMonthlyRowData, setCurrentPlanMonthlyRowData] = useState([]);
  const [currentPlanErrors, setCurrentPlanErrors] = useState({
    weekly_data_error: null,
    headers_error: null,
    monthly_data_error: null,
  });

  // Filter state
  const [currentPlanSpendView, setCurrentPlanSpendView] = useState('Weekly');
  const [currentPlanSelectedStore, setCurrentPlanSelectedStore] = useState(null);
  const [currentPlanResetDropdown] = useState(false);
  const [currentPlanIsReportDownloading, setCurrentPlanIsReportDownloading] = useState(false);

  // Channel selection state
  const [currentPlanSelectedChannels, setCurrentPlanSelectedChannels] = useState([]);
  const [currentPlanModifiedRowData, setCurrentPlanModifiedRowData] = useState([]);
  const [currentPlanModifiedMonthlyRowData, setCurrentPlanModifiedMonthlyRowData] = useState([]);
  const [currentPlanNoDataForSelectedChannel, setCurrentPlanNoDataForSelectedChannel] =
    useState(false);
  const [currentPlanShowNoChannelSelected, setCurrentPlanShowNoChannelSelected] = useState(false);
  const [currentPlanNoDataForSelectedBranch, setCurrentPlanNoDataForSelectedBranch] =
    useState(false);

  // Redux state
  const { channelsList, userStores, userData } = useSelector((state) => state.auth);

  // Filter data - stabilized
  // Stabilize the raw channel data before using in useMemo
  const stableOppsChannel = useMemo(
    () => userData?.data?.oppsChannel || [],
    [stableObject(userData?.data?.oppsChannel)]
  );

  const stableChannelsList = useMemo(() => channelsList || [], [stableObject(channelsList)]);

  // Use oppsChannel if isOppsChannel is true, otherwise use channelsList
  const sourceChannelList = useMemo(() => {
    if (isOppsChannel) {
      // Transform oppsChannel data to match channelsList format
      if (!stableOppsChannel || !Array.isArray(stableOppsChannel)) {
        return [];
      }

      try {
        return stableOppsChannel
          .filter(
            (item) => item && typeof item === 'object' && item.channel && item.channel.trim() !== ''
          ) // Filter out empty or invalid channels
          .map((item) => ({
            label: item.channel,
            value: item.channel,
          }));
      } catch (error) {
        console.error('Error transforming oppsChannel data:', error);
        return [];
      }
    }

    // Return regular channelsList, ensuring it's an array
    return Array.isArray(stableChannelsList) ? stableChannelsList : [];
  }, [isOppsChannel, stableOppsChannel, stableChannelsList]);

  const currentPlanDefaultChannelList = useMemo(() => {
    if (!sourceChannelList || !Array.isArray(sourceChannelList)) {
      return [];
    }
    return sourceChannelList.filter((channel) => channel?.label && channel?.value) || [];
  }, [sourceChannelList]);
  const currentPlanUpdatedChannelList = useMemo(
    () => currentPlanDefaultChannelList,
    [currentPlanDefaultChannelList]
  );

  // Initialize channel filter once
  const currentPlanInitRef = useRef(false);

  // Track filter changes to avoid duplicate API calls
  const currentPlanFilterRef = useRef({ store: null });

  // Store options - stabilized
  const currentPlanOptions = useMemo(
    () => formatStoreOptions(userStores),
    [stableObject(userStores)]
  );

  // Set default store on first render
  useEffect(() => {
    if (currentPlanOptions.length && !currentPlanSelectedStore) {
      setCurrentPlanSelectedStore(currentPlanOptions[0]?.value);
    }
  }, [currentPlanOptions, currentPlanSelectedStore]);

  // Initialize channel filters once
  useEffect(() => {
    if (currentPlanUpdatedChannelList?.length && !currentPlanInitRef.current) {
      currentPlanInitRef.current = true;
      setCurrentPlanSelectedChannels(currentPlanUpdatedChannelList.map((item) => item.value));
    }
  }, [currentPlanUpdatedChannelList]);

  // Fetch functions
  async function fetchWeeklyPlanSpendData() {
    if (!currentPlanSelectedStore) return null;

    try {
      setErrorsFunction(setCurrentPlanErrors);
      const response = await api.post(
        COMPONENT_QUERY + `?component=${WEEKLY_SPEND_COMPONENT}`,
        getPayload(userData?.data?.is_gale_admin, WEEKLY_SPEND_COMPONENT, currentPlanSelectedStore)
      );
      return response?.data;
    } catch (error) {
      setCurrentPlanErrors((prev) => ({ ...prev, weekly_data_error: true }));
      console.error(error);
      return null;
    }
  }

  async function fetchColumnHeaders() {
    if (!currentPlanSelectedStore) return null;

    try {
      setCurrentPlanErrors((prev) => ({ ...prev, headers_error: false }));
      const response = await api.post(
        COMPONENT_QUERY + `?component=${WEEKLY_HEADERS_COMPONENT}`,
        getPayload(true, WEEKLY_HEADERS_COMPONENT, currentPlanSelectedStore)
      );
      return response?.data;
    } catch (error) {
      setCurrentPlanErrors((prev) => ({ ...prev, headers_error: true }));
      console.error(error);
      return null;
    }
  }

  async function fetchMonthlyPlanData() {
    if (!currentPlanSelectedStore) return null;

    try {
      setErrorsFunction(setCurrentPlanErrors);
      const response = await api.post(
        COMPONENT_QUERY + `?component=${MONTHLY_SPEND_COMPONENT}`,
        getPayload(userData?.data?.is_gale_admin, MONTHLY_SPEND_COMPONENT, currentPlanSelectedStore)
      );
      return response?.data;
    } catch (error) {
      setCurrentPlanErrors((prev) => ({ ...prev, monthly_data_error: true }));
      console.error(error);
      return null;
    }
  }

  // Set up mutations
  const {
    data: weeklyPlanSpendData,
    mutate: weeklyPlanSpendMutation,
    isLoading: weeklyLoading,
  } = useMutation({
    mutationFn: fetchWeeklyPlanSpendData,
    onSuccess: () => {
      currentPlanDataFetchedRef.current.weekly = true;
      currentPlanViewSwitchTracker.current.weekly.fetched = true;
    },
  });

  const {
    data: weeklyPlanSpendTableHeaders,
    mutate: weeklyPlanSpendTableHeadersMutation,
    isLoading: headersLoading,
  } = useMutation({
    mutationFn: fetchColumnHeaders,
    onSuccess: () => {
      currentPlanDataFetchedRef.current.headers = true;
    },
  });

  const {
    data: monthlyPlanSpendData,
    mutate: monthlyPlanSpendDataMutation,
    isLoading: monthlyLoading,
  } = useMutation({
    mutationFn: fetchMonthlyPlanData,
    onSuccess: () => {
      currentPlanDataFetchedRef.current.monthly = true;
      currentPlanViewSwitchTracker.current.monthly.fetched = true;
    },
  });

  // Handler functions - stabilized
  const getCurrentPlanDataForStoreId = useCallback((storeId) => {
    setCurrentPlanSelectedStore(storeId);
  }, []);

  // Channel filtering handler
  const changeCurrentPlanSelectedFilters = useCallback((value) => {
    if (!value?.length) {
      setCurrentPlanShowNoChannelSelected(true);
      setCurrentPlanSelectedChannels([]);
      return;
    } else {
      setCurrentPlanShowNoChannelSelected(false);
    }

    setCurrentPlanSelectedChannels(value);
  }, []);

  // Updated handler for view change - stabilized
  const handleCurrentPlanSpendViewChange = useCallback(
    (event, newView) => {
      if (newView && newView !== currentPlanSpendView) {
        // Set the new view state
        setCurrentPlanSpendView(newView);

        // If switching to Monthly and monthly data hasn't been fetched yet
        if (newView === 'Monthly' && !currentPlanViewSwitchTracker.current.monthly.fetched) {
          // Show loading state when changing views
          setCurrentPlanInitialLoading(true);
          // Clear any existing monthly data
          setCurrentPlanMonthlyTableHeaders([]);
          setCurrentPlanMonthlyRowData([]);
          // Fetch monthly data
          monthlyPlanSpendDataMutation();
        }
      }
    },
    [currentPlanSpendView, monthlyPlanSpendDataMutation]
  );

  // Process weekly data when available - stabilized
  const processCurrentPlanWeeklyData = useCallback(() => {
    if (!weeklyPlanSpendData || !weeklyPlanSpendTableHeaders || !currentPlanSelectedStore) return;

    // Process headers
    if (weeklyPlanSpendTableHeaders?.data?.[WEEKLY_HEADERS_COMPONENT]?.result?.length) {
      const headerData = transformHeaders(
        weeklyPlanSpendTableHeaders.data,
        currentPlanSelectedStore,
        WEEKLY_HEADERS_COMPONENT
      );
      setCurrentPlanWeeklyTableHeaders([...channelAndAudienceHeaders, ...headerData]);
    }

    // Process row data
    if (weeklyPlanSpendData?.data?.[WEEKLY_SPEND_COMPONENT]?.result?.length) {
      const rowData = transformData(
        weeklyPlanSpendData.data,
        currentPlanSelectedStore,
        WEEKLY_SPEND_COMPONENT,
        DATA_KEY
      );
      const updatedData = verifyRowData(rowData, rowData);
      setCurrentPlanWeeklyRowData(updatedData);
    } else {
      setCurrentPlanWeeklyRowData([]);
    }
  }, [
    stableObject(weeklyPlanSpendData?.data),
    stableObject(weeklyPlanSpendTableHeaders?.data),
    currentPlanSelectedStore,
    WEEKLY_HEADERS_COMPONENT,
    WEEKLY_SPEND_COMPONENT,
  ]);

  // Process monthly data when available - stabilized
  const processCurrentPlanMonthlyData = useCallback(() => {
    if (!monthlyPlanSpendData || !currentPlanSelectedStore) return;

    if (monthlyPlanSpendData?.data?.[MONTHLY_SPEND_COMPONENT]?.result) {
      const { formattedRowData, tableHeaders } = transformMonthlyData(
        monthlyPlanSpendData.data,
        currentPlanSelectedStore,
        MONTHLY_SPEND_COMPONENT,
        DATA_KEY
      );

      const updatedRow = verifyRowData(formattedRowData, tableHeaders, currentPlanSelectedStore);

      setCurrentPlanMonthlyTableHeaders([...channelAndAudienceHeaders, ...tableHeaders]);
      setCurrentPlanMonthlyRowData(updatedRow);
    } else {
      setCurrentPlanMonthlyTableHeaders([]);
      setCurrentPlanMonthlyRowData([]);
    }
  }, [stableObject(monthlyPlanSpendData?.data), currentPlanSelectedStore, MONTHLY_SPEND_COMPONENT]);

  // Initial data loading and filter changes - streamlined
  useEffect(() => {
    if (!currentPlanSelectedStore) return;

    const shouldFetchData =
      currentPlanIsFirstMount.current ||
      currentPlanSelectedStore !== currentPlanFilterRef.current.store;

    // Save current filter state for comparison
    currentPlanFilterRef.current = {
      store: currentPlanSelectedStore,
    };

    if (shouldFetchData) {
      // Set initial loading state to true when starting to fetch data
      setCurrentPlanInitialLoading(true);
      currentPlanDataFetchInitiatedRef.current = true;

      // Reset first mount flag
      if (currentPlanIsFirstMount.current) {
        currentPlanIsFirstMount.current = false;
      }

      // Reset data tracking flags
      currentPlanDataFetchedRef.current = { weekly: false, headers: false, monthly: false };
      // Reset view switch tracker
      currentPlanViewSwitchTracker.current = {
        weekly: { fetched: false },
        monthly: { fetched: false },
      };

      // Always fetch weekly data and headers (needed for both views)
      weeklyPlanSpendMutation();
      weeklyPlanSpendTableHeadersMutation();

      // Only fetch monthly if we're on monthly view
      if (currentPlanSpendView === 'Monthly') {
        monthlyPlanSpendDataMutation();
        currentPlanViewSwitchTracker.current.monthly.fetched = true;
      }
    }
  }, [
    currentPlanSelectedStore,
    weeklyPlanSpendMutation,
    weeklyPlanSpendTableHeadersMutation,
    monthlyPlanSpendDataMutation,
    currentPlanSpendView,
  ]);

  // Process Weekly Data when available
  useEffect(() => {
    if (weeklyPlanSpendData && weeklyPlanSpendTableHeaders && currentPlanSelectedStore) {
      processCurrentPlanWeeklyData();
    }
  }, [processCurrentPlanWeeklyData]);

  // Process Monthly Data when available
  useEffect(() => {
    if (monthlyPlanSpendData && currentPlanSelectedStore) {
      processCurrentPlanMonthlyData();
    }
  }, [processCurrentPlanMonthlyData]);

  // Update filtered data when necessary data changes
  useEffect(() => {
    if (!currentPlanSelectedChannels?.length) return;

    const updateModifiedRowData = (originalData, value) => {
      if (!originalData?.length) return [];

      if (value.length === currentPlanUpdatedChannelList?.length) {
        setCurrentPlanNoDataForSelectedChannel(false);
        return originalData;
      }

      // Filter data AND exclude the Grand Total row
      const filteredData = originalData?.filter(
        (row) => value?.includes(row?.channel) && row.audience !== 'Grand Total'
      );

      const hasNoData = filteredData?.length === 0;
      setCurrentPlanNoDataForSelectedChannel(hasNoData);

      if (hasNoData) return [];

      const grandTotalColumnData = formateGrandTotalColumnsData(filteredData, isOppsChannel);
      return [...filteredData, grandTotalColumnData];
    };

    if (currentPlanSpendView === 'Weekly') {
      setCurrentPlanModifiedRowData(
        updateModifiedRowData(currentPlanWeeklyRowData, currentPlanSelectedChannels)
      );
    } else {
      setCurrentPlanModifiedMonthlyRowData(
        updateModifiedRowData(currentPlanMonthlyRowData, currentPlanSelectedChannels)
      );
    }
  }, [
    currentPlanWeeklyRowData,
    currentPlanMonthlyRowData,
    currentPlanSelectedChannels,
    currentPlanSpendView,
    currentPlanUpdatedChannelList,
  ]);

  // Check if there is data for selected branch
  useEffect(() => {
    // Determine which row data to use based on view and modified data availability
    const getRowData = () => {
      if (currentPlanSpendView === 'Weekly') {
        return currentPlanModifiedRowData.length
          ? currentPlanModifiedRowData
          : currentPlanWeeklyRowData;
      } else {
        return currentPlanModifiedMonthlyRowData.length
          ? currentPlanModifiedMonthlyRowData
          : currentPlanMonthlyRowData;
      }
    };
    const currentRows = getRowData();
    const currentCols =
      currentPlanSpendView === 'Weekly'
        ? currentPlanWeeklyTableHeaders
        : currentPlanMonthlyTableHeaders;
    const hasData = currentRows?.length > 1 && currentCols?.length >= 4;
    setCurrentPlanNoDataForSelectedBranch(!hasData);
  }, [
    currentPlanSpendView,
    currentPlanModifiedRowData,
    currentPlanModifiedMonthlyRowData,
    currentPlanWeeklyRowData,
    currentPlanMonthlyRowData,
    currentPlanWeeklyTableHeaders,
    currentPlanMonthlyTableHeaders,
  ]);

  // Track loading state and turn off initialLoading when data is ready
  useEffect(() => {
    // Only check loading states after data fetch has been initiated
    if (!currentPlanDataFetchInitiatedRef.current) return;

    if (currentPlanSpendView === 'Weekly') {
      // For Weekly view - both weekly data and headers are needed
      if (
        (!weeklyLoading &&
          !headersLoading &&
          currentPlanDataFetchedRef.current.weekly &&
          currentPlanDataFetchedRef.current.headers) ||
        currentPlanErrors.weekly_data_error ||
        currentPlanErrors.headers_error
      ) {
        setCurrentPlanInitialLoading(false);
      }
    } else if (
      // For Monthly view - only monthly data is needed
      (!monthlyLoading && currentPlanDataFetchedRef.current.monthly) ||
      currentPlanErrors.monthly_data_error
    ) {
      setCurrentPlanInitialLoading(false);
    }
  }, [
    currentPlanSpendView,
    weeklyLoading,
    headersLoading,
    monthlyLoading,
    currentPlanDataFetchedRef.current.weekly,
    currentPlanDataFetchedRef.current.headers,
    currentPlanDataFetchedRef.current.monthly,
    currentPlanErrors.weekly_data_error,
    currentPlanErrors.headers_error,
    currentPlanErrors.monthly_data_error,
  ]);

  // Determine if download button should be disabled
  const isCurrentPlanDownloadDisabled = useMemo(
    () =>
      currentPlanInitialLoading ||
      weeklyLoading ||
      headersLoading ||
      monthlyLoading ||
      currentPlanSelectedChannels?.length === 0 ||
      currentPlanNoDataForSelectedChannel ||
      currentPlanErrors.weekly_data_error ||
      currentPlanErrors.headers_error ||
      currentPlanErrors.monthly_data_error ||
      currentPlanShowNoChannelSelected ||
      currentPlanNoDataForSelectedBranch,
    [
      currentPlanInitialLoading,
      weeklyLoading,
      headersLoading,
      monthlyLoading,
      currentPlanSelectedChannels?.length,
      currentPlanNoDataForSelectedChannel,
      currentPlanErrors.weekly_data_error,
      currentPlanErrors.headers_error,
      currentPlanErrors.monthly_data_error,
      currentPlanShowNoChannelSelected,
      currentPlanNoDataForSelectedBranch,
    ]
  );

  // Build download props
  const getCurrentPlanDownloadProps = useCallback(() => {
    return {
      component:
        currentPlanSpendView === 'Weekly' ? WEEKLY_SPEND_COMPONENT : MONTHLY_SPEND_COMPONENT,
      appliedFilters: appendChannelFilter(
        getPayload(
          userData?.data?.is_gale_admin,
          currentPlanSpendView === 'Weekly' ? WEEKLY_SPEND_COMPONENT : MONTHLY_SPEND_COMPONENT,
          currentPlanSelectedStore,
          null,
          true
        ),
        currentPlanSelectedChannels,
        currentPlanUpdatedChannelList
      ),
      filename:
        currentPlanSpendView === 'Weekly' ? DOWNLOAD_FILE_NAME_WEEKLY : DOWNLOAD_FILE_NAME_MONTHLY,
      setIsDownloading: setCurrentPlanIsReportDownloading,
      isDownloading: currentPlanIsReportDownloading,
      disabled: isCurrentPlanDownloadDisabled,
      customButtonStyle: { marginTop: '8px' },
    };
  }, [
    currentPlanSpendView,
    WEEKLY_SPEND_COMPONENT,
    MONTHLY_SPEND_COMPONENT,
    DOWNLOAD_FILE_NAME_WEEKLY,
    DOWNLOAD_FILE_NAME_MONTHLY,
    currentPlanSelectedStore,
    currentPlanSelectedChannels,
    currentPlanUpdatedChannelList,
    userData?.data?.is_gale_admin,
    currentPlanIsReportDownloading,
    isCurrentPlanDownloadDisabled,
  ]);

  return {
    // Data state
    weeklyTableHeaders: currentPlanWeeklyTableHeaders,
    weeklyRowData: currentPlanWeeklyRowData,
    monthlyTableHeaders: currentPlanMonthlyTableHeaders,
    monthlyRowData: currentPlanMonthlyRowData,
    errors: currentPlanErrors,
    // Filter state
    selectedStore: currentPlanSelectedStore,
    selectedChannels: currentPlanSelectedChannels,
    planSpendView: currentPlanSpendView,
    resetDropdown: currentPlanResetDropdown,
    // Filter data
    updatedChannelList: currentPlanUpdatedChannelList,
    options: currentPlanOptions,
    // Loading states
    weeklyLoading,
    headersLoading,
    monthlyLoading,
    initialLoading: currentPlanInitialLoading,
    // Channel selection state and logic
    modifiedRowData: currentPlanModifiedRowData,
    modifiedMonthlyRowData: currentPlanModifiedMonthlyRowData,
    noDataForSelectedChannel: currentPlanNoDataForSelectedChannel,
    showNoChannelSelected: currentPlanShowNoChannelSelected,
    // Handler functions
    getDataForStoreId: getCurrentPlanDataForStoreId,
    handlePlanSpendViewChange: handleCurrentPlanSpendViewChange,
    changeSelectedFilters: changeCurrentPlanSelectedFilters,
    // Download props
    getDownloadProps: getCurrentPlanDownloadProps,
    // Additional data for graphs
    userData,
    userStores,
  };
};

export default useCurrentPlanSpendData;
